// Analytics utility for tracking user interactions
// Supports Google Analytics (gtag), GTM dataLayer, Meta Pixel, and custom event handlers

type EventParams = {
  category: string;
  action: string;
  label?: string;
  value?: number;
};

// GTM Conversion Event Names (use these in GTM triggers)
export const GTM_EVENTS = {
  // Form Submissions
  FORM_SUBMIT_QUOTE: 'bbq_form_submit_quote',
  FORM_SUBMIT_CONTACT: 'bbq_form_submit_contact',
  FORM_SUBMIT_WIZARD: 'bbq_form_submit_wizard',
  FORM_SUBMIT_TRADE: 'bbq_form_submit_trade',
  FORM_SUBMIT_SMS: 'bbq_form_submit_sms',
  FORM_SUBMIT_APPOINTMENT: 'bbq_form_submit_appointment',
  
  // Phone Clicks
  PHONE_CLICK_HEADER: 'bbq_phone_click_header',
  PHONE_CLICK_FOOTER: 'bbq_phone_click_footer',
  PHONE_CLICK_MOBILE_CTA: 'bbq_phone_click_mobile_cta',
  PHONE_CLICK_MODAL: 'bbq_phone_click_modal',
  
  // Quote/CTA Actions
  QUOTE_MODAL_OPEN: 'bbq_quote_modal_open',
  QUOTE_REQUEST_START: 'bbq_quote_request_start',
  QUOTE_REQUEST_COMPLETE: 'bbq_quote_request_complete',
  
  // Engagement
  WIZARD_START: 'bbq_wizard_start',
  WIZARD_STEP: 'bbq_wizard_step',
  WIZARD_COMPLETE: 'bbq_wizard_complete',
  VIDEO_PLAY: 'bbq_video_play',
  VIDEO_COMPLETE: 'bbq_video_complete',
  SAVINGS_MODAL_OPEN: 'bbq_savings_modal_open',
  
  // Scroll & Time
  SCROLL_DEPTH: 'bbq_scroll_depth',
  TIME_ON_PAGE: 'bbq_time_on_page',
  
  // CTA & Navigation
  BUTTON_CLICK: 'bbq_button_click',
  CTA_CLICK: 'bbq_cta_click',
  NAV_CLICK: 'bbq_nav_click',
  OUTBOUND_LINK: 'bbq_outbound_link',
  SECTION_VIEW: 'bbq_section_view',
  
  // Design Wizard CTA
  DESIGN_WIZARD_CTA_CLICK: 'bbq_design_wizard_cta_click',
} as const;

// Estimated conversion values for ROI tracking (in USD)
// Based on: Average order value ~$8,500, varying close rates by lead type
export const CONVERSION_VALUES: Record<string, number> = {
  // Phone calls - highest intent, ~15% close rate → $8,500 × 0.15 = $1,275
  PHONE_CALL: 150,
  
  // Quote form submissions - high intent, ~10% close rate → $8,500 × 0.10 = $850
  QUOTE_FORM: 100,
  
  // Contact form submissions - medium intent, ~5% close rate
  CONTACT_FORM: 50,
  
  // Design wizard completions - very high intent, ~20% close rate
  WIZARD_COMPLETE: 200,
  
  // Wizard start - shows interest, ~2% eventual close rate
  WIZARD_START: 20,
  
  // Quote modal open - initial interest
  QUOTE_MODAL_OPEN: 10,
  
  // Appointment scheduled - very high intent, ~25% close rate
  APPOINTMENT_SCHEDULED: 250,
  
  // Trade application - B2B lead, higher average order
  TRADE_APPLICATION: 300,
};

// Currency for conversion values
const CONVERSION_CURRENCY = 'USD';

// Google Ads Conversion Labels (format: conversionId/conversionLabel)
// Main lead form conversion label from Google Ads
const GOOGLE_ADS_CONVERSION_LABELS = {
  LEAD_FORM: 'oC3mCMK9_eYbENaAhM9C',           // Submit lead form
  PHONE_CALL: 'oC3mCMK9_eYbENaAhM9C',          // Phone clicks (same as lead)
  QUOTE_REQUEST: 'oC3mCMK9_eYbENaAhM9C',       // Quote requests
  WIZARD_COMPLETE: 'oC3mCMK9_eYbENaAhM9C',     // Wizard completions
  APPOINTMENT: 'oC3mCMK9_eYbENaAhM9C',         // Appointment scheduled
} as const;

// Check if gtag is available (Google Analytics)
const hasGtag = (): boolean => {
  return typeof window !== 'undefined' && typeof (window as any).gtag === 'function';
};

// Check if Meta Pixel is available
const hasFbq = (): boolean => {
  return typeof window !== 'undefined' && typeof (window as any).fbq === 'function';
};

// Track Google Ads conversion
const trackGoogleAdsConversion = (
  conversionLabel: string,
  value: number = 1.0,
  url?: string
): void => {
  if (typeof window !== 'undefined' && typeof (window as any).gtag_report_conversion === 'function') {
    (window as any).gtag_report_conversion(conversionLabel, value, url);
  } else if (hasGtag()) {
    // Fallback: directly call gtag if gtag_report_conversion isn't available
    (window as any).gtag('event', 'conversion', {
      'send_to': `AW-17882480726/${conversionLabel}`,
      'value': value,
      'currency': 'USD',
    });
  }
  
  if (import.meta.env.DEV) {
    console.log('[Google Ads Conversion]', { conversionLabel, value });
  }
};

// Push event to GTM dataLayer
const pushToDataLayer = (eventName: string, eventData: Record<string, unknown> = {}): void => {
  if (typeof window !== 'undefined') {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: eventName,
      ...eventData,
    });
  }
};

// Push conversion with value to GTM (for GA4 conversion tracking)
const pushConversionToDataLayer = (
  eventName: string, 
  value: number, 
  eventData: Record<string, unknown> = {}
): void => {
  pushToDataLayer(eventName, {
    ...eventData,
    value,
    currency: CONVERSION_CURRENCY,
    conversion_value: value,
  });
};

// Track Meta Pixel event with value
const trackMetaEventWithValue = (
  eventName: string,
  value: number,
  eventData: Record<string, unknown> = {}
): void => {
  if (hasFbq()) {
    (window as any).fbq('track', eventName, {
      ...eventData,
      value,
      currency: CONVERSION_CURRENCY,
    });
  }
  
  if (import.meta.env.DEV) {
    console.log('[Meta Pixel + Value]', eventName, { value, currency: CONVERSION_CURRENCY, ...eventData });
  }
};

// Track an event (unified tracking across all platforms)
export const trackEvent = ({ category, action, label, value }: EventParams): void => {
  // Log to console in development
  if (import.meta.env.DEV) {
    console.log('[Analytics]', { category, action, label, value });
  }

  // Push to GTM dataLayer for custom triggers
  pushToDataLayer('custom_event', {
    event_category: category,
    event_action: action,
    event_label: label,
    event_value: value,
  });

  // Send to Google Analytics if available
  if (hasGtag()) {
    (window as any).gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }

  // Dispatch custom event for any listeners
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('analytics_event', {
        detail: { category, action, label, value },
      })
    );
  }
};

// Track GTM conversion event with proper naming
export const trackConversionEvent = (
  eventName: string, 
  eventData: Record<string, unknown> = {}
): void => {
  if (import.meta.env.DEV) {
    console.log('[GTM Conversion]', eventName, eventData);
  }
  
  pushToDataLayer(eventName, eventData);
};

// Track Meta Pixel event
export const trackMetaEvent = (
  eventName: string, 
  eventData: Record<string, unknown> = {}
): void => {
  if (hasFbq()) {
    (window as any).fbq('track', eventName, eventData);
  }
  
  if (import.meta.env.DEV) {
    console.log('[Meta Pixel]', eventName, eventData);
  }
};

// ============================================
// Predefined tracking functions (BBQ brand)
// ============================================

// Phone click tracking with GTM + Meta + Conversion Value
export const trackPhoneClick = (brand: 'bbq', source: string): void => {
  const eventName = `bbq_phone_click_${source}`;
  const conversionValue = CONVERSION_VALUES.PHONE_CALL;
  
  trackEvent({
    category: 'engagement',
    action: 'phone_click',
    label: `${brand}_${source}`,
    value: conversionValue,
  });
  
  // GTM conversion event with value
  pushConversionToDataLayer(eventName, conversionValue, {
    brand,
    source,
    conversion_type: 'phone_click',
  });
  
  // Meta Pixel - Contact event with value
  trackMetaEventWithValue('Contact', conversionValue, {
    content_category: 'phone_call',
    content_name: source,
  });
  
  // Google Ads conversion
  trackGoogleAdsConversion(GOOGLE_ADS_CONVERSION_LABELS.PHONE_CALL, conversionValue);
};

// Form submission tracking with GTM + Meta + Conversion Value
export const trackFormSubmission = (brand: 'bbq', formType: string): void => {
  const eventName = `bbq_form_submit_${formType}`;
  
  // Determine conversion value based on form type
  let conversionValue = CONVERSION_VALUES.CONTACT_FORM;
  let googleAdsLabel = GOOGLE_ADS_CONVERSION_LABELS.LEAD_FORM;
  
  if (formType === 'quote') {
    conversionValue = CONVERSION_VALUES.QUOTE_FORM;
    googleAdsLabel = GOOGLE_ADS_CONVERSION_LABELS.QUOTE_REQUEST;
  }
  if (formType === 'wizard') {
    conversionValue = CONVERSION_VALUES.WIZARD_COMPLETE;
    googleAdsLabel = GOOGLE_ADS_CONVERSION_LABELS.WIZARD_COMPLETE;
  }
  if (formType === 'trade') conversionValue = CONVERSION_VALUES.TRADE_APPLICATION;
  if (formType === 'appointment') {
    conversionValue = CONVERSION_VALUES.APPOINTMENT_SCHEDULED;
    googleAdsLabel = GOOGLE_ADS_CONVERSION_LABELS.APPOINTMENT;
  }
  
  trackEvent({
    category: 'conversion',
    action: 'form_submit',
    label: `${brand}_${formType}`,
    value: conversionValue,
  });
  
  // GTM conversion event with value
  pushConversionToDataLayer(eventName, conversionValue, {
    brand,
    form_type: formType,
    conversion_type: 'form_submission',
  });
  
  // Meta Pixel - Lead event with value
  trackMetaEventWithValue('Lead', conversionValue, {
    content_category: 'form_submission',
    content_name: formType,
  });
  
  // Google Ads conversion
  trackGoogleAdsConversion(googleAdsLabel, conversionValue);
};

// Quote request tracking with GTM + Meta + Conversion Value + Google Ads
export const trackQuoteRequest = (stage: 'start' | 'complete', details?: Record<string, unknown>): void => {
  const eventName = stage === 'start' 
    ? GTM_EVENTS.QUOTE_REQUEST_START 
    : GTM_EVENTS.QUOTE_REQUEST_COMPLETE;
  
  const conversionValue = stage === 'start' 
    ? CONVERSION_VALUES.QUOTE_MODAL_OPEN 
    : CONVERSION_VALUES.QUOTE_FORM;
  
  trackEvent({
    category: 'conversion',
    action: `quote_request_${stage}`,
    label: 'bbq_quote',
    value: conversionValue,
  });
  
  // GTM conversion event with value
  pushConversionToDataLayer(eventName, conversionValue, {
    stage,
    ...details,
  });
  
  // Meta Pixel - InitiateCheckout for start, Lead for complete
  if (stage === 'start') {
    trackMetaEventWithValue('InitiateCheckout', conversionValue, { 
      content_category: 'quote_request' 
    });
  } else {
    trackMetaEventWithValue('Lead', conversionValue, { 
      content_category: 'quote_request',
      content_name: 'quote_complete',
    });
    
    // Google Ads conversion - only on complete
    trackGoogleAdsConversion(GOOGLE_ADS_CONVERSION_LABELS.QUOTE_REQUEST, conversionValue);
  }
};

// Modal open tracking with conversion value
export const trackModalOpen = (modalType: 'quote' | 'savings'): void => {
  const eventName = modalType === 'quote' 
    ? GTM_EVENTS.QUOTE_MODAL_OPEN 
    : GTM_EVENTS.SAVINGS_MODAL_OPEN;
  
  const conversionValue = CONVERSION_VALUES.QUOTE_MODAL_OPEN;
  
  pushConversionToDataLayer(eventName, conversionValue, { 
    modal_type: modalType 
  });
  
  // Meta Pixel - ViewContent for modal opens
  trackMetaEventWithValue('ViewContent', conversionValue, {
    content_type: 'modal',
    content_name: modalType,
  });
};

// Navigation tracking (no conversion value - engagement only)
export const trackNavigation = (brand: 'bbq', destination: string): void => {
  trackEvent({
    category: 'navigation',
    action: 'link_click',
    label: `${brand}_${destination}`,
  });
};

// Wizard tracking with conversion values
export const trackWizardEvent = (stage: 'start' | 'step' | 'complete', stepData?: Record<string, unknown>): void => {
  let eventName: string = GTM_EVENTS.WIZARD_START;
  let conversionValue = CONVERSION_VALUES.WIZARD_START;
  
  if (stage === 'step') {
    eventName = GTM_EVENTS.WIZARD_STEP;
    conversionValue = 0; // No value for individual steps
  }
  if (stage === 'complete') {
    eventName = GTM_EVENTS.WIZARD_COMPLETE;
    conversionValue = CONVERSION_VALUES.WIZARD_COMPLETE;
  }
  
  // GTM conversion event with value
  pushConversionToDataLayer(eventName, conversionValue, {
    stage,
    ...stepData,
  });
  
  if (stage === 'start') {
    trackMetaEventWithValue('InitiateCheckout', conversionValue, {
      content_category: 'design_wizard',
      content_name: 'wizard_start',
    });
  }
  
  if (stage === 'complete') {
    trackMetaEventWithValue('CompleteRegistration', conversionValue, {
      content_category: 'design_wizard',
    });
  }
};

// Video tracking (engagement, no conversion value)
export const trackVideoEvent = (
  action: 'play' | 'complete' | 'progress',
  videoData: { videoId: string; title?: string; progress?: number }
): void => {
  const eventName = action === 'complete' ? GTM_EVENTS.VIDEO_COMPLETE : GTM_EVENTS.VIDEO_PLAY;
  
  trackEvent({
    category: 'video',
    action: `video_${action}`,
    label: videoData.videoId,
    value: videoData.progress,
  });
  
  trackConversionEvent(eventName, {
    video_id: videoData.videoId,
    video_title: videoData.title,
    progress: videoData.progress,
  });
  
  if (action === 'play') {
    trackMetaEvent('ViewContent', {
      content_type: 'video',
      content_ids: [videoData.videoId],
    });
  }
};

// CTA button click tracking (engagement, no conversion value)
export const trackCTAClick = (
  ctaName: string, 
  ctaLocation: string, 
  destination?: string
): void => {
  trackEvent({
    category: 'cta',
    action: 'cta_click',
    label: `${ctaLocation}_${ctaName}`,
  });
  
  trackConversionEvent(GTM_EVENTS.CTA_CLICK, {
    cta_name: ctaName,
    cta_location: ctaLocation,
    destination,
  });
};

// Design Wizard CTA tracking with conversion value
export const trackDesignWizardCTA = (variant: string, location: string): void => {
  const conversionValue = CONVERSION_VALUES.WIZARD_START;
  
  trackEvent({
    category: 'cta',
    action: 'design_wizard_cta_click',
    label: `${location}_${variant}`,
    value: conversionValue,
  });
  
  pushConversionToDataLayer(GTM_EVENTS.DESIGN_WIZARD_CTA_CLICK, conversionValue, {
    variant,
    location,
  });
  
  trackMetaEventWithValue('InitiateCheckout', conversionValue, {
    content_category: 'design_wizard',
    content_name: `${variant}_cta`,
  });
};

// Appointment scheduling with conversion value
export const trackAppointmentScheduled = (details?: Record<string, unknown>): void => {
  const conversionValue = CONVERSION_VALUES.APPOINTMENT_SCHEDULED;
  
  trackEvent({
    category: 'conversion',
    action: 'appointment_scheduled',
    label: 'bbq_appointment',
    value: conversionValue,
  });
  
  pushConversionToDataLayer(GTM_EVENTS.FORM_SUBMIT_APPOINTMENT, conversionValue, {
    conversion_type: 'appointment',
    ...details,
  });
  
  trackMetaEventWithValue('Schedule', conversionValue, {
    content_category: 'appointment',
  });
};
